import React from 'react';
import { Lock, Sparkles } from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';

/**
 * LessonNode - Anvil with 4 corner flames and decay/moss system
 * 
 * DECAY SYSTEM:
 * - Gold with 4 torches → 3 → 2 → 1 torch
 * - Silver with 4 torches → 3 → 2 → 1 torch
 * - Bronze with 4 torches → 3 → 2 → 1 torch
 * - Iron with 4 torches → 3 → 2 → 1 torch
 * 
 * Total 16 decay states. Moss grows as decay increases.
 * User must complete one level to remove moss and restore progress.
 */

// Anvil images for each level state
const ANVIL_IMAGES = {
  locked: '/images/anvils/anvil_iron.png',
  iron: '/images/anvils/anvil_iron.png',
  bronze: '/images/anvils/anvil_bronze.png',
  silver: '/images/anvils/anvil_silver.png',
  gold: '/images/anvils/anvil_gold.png'
};

// Moss SVG overlay component - grows based on decay level
const MossOverlay = ({ decayLevel = 0 }) => {
  // decayLevel 0 = no moss, 16 = full moss coverage
  if (decayLevel === 0) return null;
  
  const opacity = Math.min(0.7, decayLevel * 0.05);
  const coverage = Math.min(100, decayLevel * 7);
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-full">
      {/* Base moss layer */}
      <svg 
        viewBox="0 0 100 100" 
        className="absolute inset-0 w-full h-full"
        style={{ opacity }}
      >
        <defs>
          <radialGradient id="moss-gradient" cx="50%" cy="100%" r="80%">
            <stop offset="0%" stopColor="#1a472a" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#2d5a3f" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#3d6b4f" stopOpacity="0" />
          </radialGradient>
          <filter id="moss-texture">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" result="noise"/>
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" />
          </filter>
        </defs>
        
        {/* Growing moss from bottom */}
        <ellipse 
          cx="50" 
          cy={100 - coverage * 0.3} 
          rx={20 + coverage * 0.4} 
          ry={10 + coverage * 0.3}
          fill="url(#moss-gradient)"
          filter="url(#moss-texture)"
        />
        
        {/* Side moss patches */}
        {decayLevel > 4 && (
          <>
            <ellipse 
              cx="20" 
              cy={80 - coverage * 0.2} 
              rx={8 + coverage * 0.15} 
              ry={6 + coverage * 0.1}
              fill="#2d5a3f"
              opacity={Math.min(0.8, decayLevel * 0.06)}
            />
            <ellipse 
              cx="80" 
              cy={85 - coverage * 0.15} 
              rx={7 + coverage * 0.12} 
              ry={5 + coverage * 0.08}
              fill="#1a472a"
              opacity={Math.min(0.7, decayLevel * 0.05)}
            />
          </>
        )}
        
        {/* Top moss patches for heavy decay */}
        {decayLevel > 10 && (
          <>
            <ellipse 
              cx="35" 
              cy={40 - coverage * 0.1} 
              rx={6 + coverage * 0.1} 
              ry={4 + coverage * 0.05}
              fill="#3d6b4f"
              opacity={Math.min(0.6, (decayLevel - 10) * 0.08)}
            />
            <ellipse 
              cx="65" 
              cy={45 - coverage * 0.08} 
              rx={5 + coverage * 0.08} 
              ry={3 + coverage * 0.04}
              fill="#2d5a3f"
              opacity={Math.min(0.5, (decayLevel - 10) * 0.06)}
            />
          </>
        )}
      </svg>
      
      {/* Moss speckles */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 30% 70%, rgba(45, 90, 63, ${opacity * 0.5}) 0%, transparent 30%),
                       radial-gradient(circle at 70% 80%, rgba(29, 71, 42, ${opacity * 0.4}) 0%, transparent 25%),
                       radial-gradient(circle at 50% 90%, rgba(61, 107, 79, ${opacity * 0.6}) 0%, transparent 35%)`,
        }}
      />
      
      {/* Subtle green tint overlay */}
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          background: `linear-gradient(to top, rgba(34, 85, 51, ${opacity * 0.3}) 0%, transparent 60%)`,
        }}
      />
    </div>
  );
};

// Flame SVG component - positioned closer to the icon
const Flame = ({ lit = false, position = 'top-left', size = 24 }) => {
  const positionClasses = {
    'top-left': '-top-1 -left-1',
    'top-right': '-top-1 -right-1',
    'bottom-left': 'bottom-0 -left-1',
    'bottom-right': 'bottom-0 -right-1'
  };

  return (
    <div 
      className={`absolute ${positionClasses[position]} transition-all duration-500`}
      style={{ width: size, height: size }}
    >
      <svg 
        viewBox="0 0 24 24" 
        className={`w-full h-full transition-all duration-500 ${
          lit ? 'opacity-100' : 'opacity-30'
        }`}
        style={{
          filter: lit 
            ? 'drop-shadow(0 0 6px rgba(251, 146, 60, 0.8)) drop-shadow(0 0 12px rgba(251, 146, 60, 0.4))' 
            : 'grayscale(100%) brightness(0.5)'
        }}
      >
        <defs>
          <linearGradient id={`flame-gradient-${position}-${lit}`} x1="0%" y1="100%" x2="0%" y2="0%">
            {lit ? (
              <>
                <stop offset="0%" stopColor="#dc2626" />
                <stop offset="30%" stopColor="#f97316" />
                <stop offset="60%" stopColor="#fbbf24" />
                <stop offset="100%" stopColor="#fef3c7" />
              </>
            ) : (
              <>
                <stop offset="0%" stopColor="#1f2937" />
                <stop offset="50%" stopColor="#374151" />
                <stop offset="100%" stopColor="#4b5563" />
              </>
            )}
          </linearGradient>
        </defs>
        <path
          d="M12 2C12 2 8 7 8 11C8 13.5 9.5 15.5 11 16.5C10 15.5 9.5 14 10 12.5C10.5 11 12 10 12 10C12 10 13.5 11 14 12.5C14.5 14 14 15.5 13 16.5C14.5 15.5 16 13.5 16 11C16 7 12 2 12 2Z"
          fill={`url(#flame-gradient-${position}-${lit})`}
        />
        {lit && (
          <path
            d="M12 6C12 6 10 9 10 11C10 12.5 11 13.5 12 14C11.5 13.5 11 12.5 11.5 11.5C12 10.5 12 10 12 10C12 10 12 10.5 12.5 11.5C13 12.5 12.5 13.5 12 14C13 13.5 14 12.5 14 11C14 9 12 6 12 6Z"
            fill="#fef3c7"
            opacity="0.8"
          />
        )}
      </svg>
      {lit && (
        <div 
          className="absolute inset-0 animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(251,191,36,0.3) 0%, transparent 70%)'
          }}
        />
      )}
    </div>
  );
};

// Theme to i18n key mapping
const THEME_I18N_KEYS = {
  'Introduction & Greetings': 'forge.introductionGreetings',
  'Family': 'forge.familyRelations',
  'Occupation': 'forge.workProfessions',
  'Recreation': 'forge.hobbiesLeisure',
  'Travel': 'forge.travelTransport',
  'Education': 'forge.educationLearning',
  'The Reforge': 'forge.theReforge',
  'Daily Routines': 'forge.dailyActivities',
  'Home & Living': 'forge.homeHousehold',
  'Health & Body': 'forge.health',
  'Food & Dining': 'forge.foodDrink',
  'Shopping & Money': 'forge.shopping',
  'Nature & Weather': 'forge.natureEnvironment',
  'Emotions & Feelings': 'forge.emotionsFeelings',
  'Social Situations': 'forge.socialActivities',
  'Technology & Media': 'forge.communication',
  'Arts & Culture': 'forge.artsEntertainment',
  'Government & Society': 'forge.advancedTopics',
  'Abstract Concepts': 'forge.advancedTopics',
  'Professional Life': 'forge.workProfessions',
  'Academic Language': 'forge.educationLearning',
  'Current Events': 'forge.advancedTopics',
  'Philosophy & Ideas': 'forge.advancedTopics',
  'Specialized Vocabulary': 'forge.advancedTopics',
  'Idiomatic Expressions': 'forge.advancedTopics',
  'The Grand Forge': 'forge.theGrandForge',
  'Castle Gate': 'forge.theReforge',
};

// Theme icon mapping
const THEME_ICONS = {
  'Intro': '👋',
  'Greetings': '🤝',
  'Family': '👨‍👩‍👧‍👦',
  'Numbers': '🔢',
  'Food': '🍽️',
  'Travel': '✈️',
  'Work': '💼',
  'Home': '🏠',
  'Nature': '🌲',
  'Education': '📚',
  'Health': '💊',
  'Shopping': '🛒',
  'Weather': '☀️',
  'Time': '⏰',
  'Castle Gate': '🏰',
  'Introduction & Greetings': '👋',
  'Occupation': '💼',
  'Recreation': '🎮',
  'The Reforge': '🏰',
  'Daily Routines': '☀️',
  'Home & Living': '🏠',
  'Health & Body': '💊',
  'Food & Dining': '🍽️',
  'Shopping & Money': '💰',
  'Nature & Weather': '🌿',
  'Emotions & Feelings': '❤️',
  'Social Situations': '💬',
  'Technology & Media': '📱',
  'Arts & Culture': '🎨',
  'Government & Society': '🏛️',
  'Abstract Concepts': '🧠',
  'Professional Life': '🏢',
  'Academic Language': '📖',
  'Current Events': '📰',
  'Philosophy & Ideas': '💡',
  'Specialized Vocabulary': '🔬',
  'Idiomatic Expressions': '💭',
  'The Grand Forge': '👑'
};

/**
 * Calculate decay state from decay level (0-16)
 * 0 = no decay (pristine gold with 4 torches)
 * 1-4 = gold with decreasing torches
 * 5-8 = silver with decreasing torches
 * 9-12 = bronze with decreasing torches
 * 13-16 = iron with decreasing torches
 */
const getDecayState = (decayLevel, originalLevel) => {
  // If no decay or not completed, return original state
  if (decayLevel === 0 || !originalLevel) {
    return { level: originalLevel, torches: 4, hasMoss: false };
  }
  
  // Calculate which level and how many torches based on decay
  const levels = ['gold', 'silver', 'bronze', 'iron'];
  const originalIndex = levels.indexOf(originalLevel);
  
  if (originalIndex === -1) {
    return { level: originalLevel, torches: 4, hasMoss: false };
  }
  
  // Each level has 4 torch states (4, 3, 2, 1)
  // decayLevel 1-4 = same level, decreasing torches
  // decayLevel 5-8 = next level down, decreasing torches
  // etc.
  
  const levelDrop = Math.floor((decayLevel - 1) / 4);
  const torchesLost = ((decayLevel - 1) % 4) + 1;
  const torches = 5 - torchesLost; // 4, 3, 2, 1
  
  const newLevelIndex = Math.min(originalIndex + levelDrop, levels.length - 1);
  const newLevel = levels[newLevelIndex];
  
  return {
    level: newLevel,
    torches: Math.max(1, torches),
    hasMoss: decayLevel > 0,
    decayLevel
  };
};

export default function LessonNode({
  lessonNumber,
  title,
  theme,
  state = 'locked',
  isCurrent = false,
  levelsCompleted = { iron: false, bronze: false, silver: false, gold: false },
  currentLevelSteps = 0,
  decayLevel = 0, // 0-16 decay level
  blacksmithImage,
  onClick,
  position = 'left',
  scale = 1,
  priority = null // { score, reason, mastery_pct, breakdown, rank }
}) {
  const { t } = useI18n();
  const isLocked = state === 'locked';
  const themeEmoji = THEME_ICONS[theme] || '📖';
  
  // Get translated theme name
  const translatedTheme = THEME_I18N_KEYS[theme] 
    ? t(THEME_I18N_KEYS[theme]) 
    : theme;
  
  // Determine the highest completed level
  let highestLevel = null;
  if (levelsCompleted.gold) highestLevel = 'gold';
  else if (levelsCompleted.silver) highestLevel = 'silver';
  else if (levelsCompleted.bronze) highestLevel = 'bronze';
  else if (levelsCompleted.iron) highestLevel = 'iron';
  
  // Apply decay to the display state
  const decayState = getDecayState(decayLevel, highestLevel);
  
  // Determine which anvil image to show
  let anvilImage = ANVIL_IMAGES.iron;
  let displayLevel = state;
  
  if (isLocked) {
    anvilImage = ANVIL_IMAGES.locked;
  } else if (decayLevel > 0 && highestLevel) {
    // Show decayed level
    anvilImage = ANVIL_IMAGES[decayState.level];
    displayLevel = decayState.level;
  } else if (levelsCompleted.gold) {
    anvilImage = ANVIL_IMAGES.gold;
    displayLevel = 'gold';
  } else if (levelsCompleted.silver) {
    anvilImage = ANVIL_IMAGES.silver;
    displayLevel = 'silver';
  } else if (levelsCompleted.bronze) {
    anvilImage = ANVIL_IMAGES.bronze;
    displayLevel = 'bronze';
  } else if (levelsCompleted.iron) {
    anvilImage = ANVIL_IMAGES.bronze; // After iron, show bronze (next target)
    displayLevel = 'bronze';
  }

  // Calculate lit torches
  let litTorches = 0;
  
  if (isLocked) {
    litTorches = 0;
  } else if (decayLevel > 0 && highestLevel) {
    // Decayed state - show decay torches
    litTorches = decayState.torches;
  } else if (levelsCompleted.gold) {
    litTorches = 4; // Fully lit when gold complete
  } else {
    // Show current level progress
    litTorches = Math.min(currentLevelSteps, 4);
  }

  return (
    <div 
      className={`relative flex items-center gap-2 ${
        position === 'left' ? 'flex-row' : 'flex-row-reverse'
      }`}
      style={{ transform: `scale(${scale})` }}
    >
      {/* Anvil Node Button with Flames */}
      <button
        onClick={() => !isLocked && onClick?.()}
        disabled={isLocked}
        className={`
          relative flex items-center justify-center
          transition-all duration-300 ease-out
          ${!isLocked ? 'hover:scale-110 active:scale-95 cursor-pointer' : 'cursor-not-allowed opacity-60'}
          ${isCurrent ? 'scale-110' : ''}
        `}
        data-testid={`node-${lessonNumber}`}
        aria-label={`Lesson ${lessonNumber}: ${title}${decayLevel > 0 ? ' (decayed)' : ''}`}
        style={{
          filter: isCurrent 
            ? 'drop-shadow(0 0 20px rgba(251, 191, 36, 0.6))' 
            : 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))'
        }}
      >
        {/* Anvil Image Container */}
        <div className="relative w-16 h-16 md:w-20 md:h-20">
          {/* Four corner flames - lit based on decay/progress */}
          <Flame lit={litTorches >= 1} position="top-left" size={24} />
          <Flame lit={litTorches >= 2} position="top-right" size={24} />
          <Flame lit={litTorches >= 3} position="bottom-left" size={24} />
          <Flame lit={litTorches >= 4} position="bottom-right" size={24} />
          
          {/* Anvil image */}
          <img 
            src={anvilImage}
            alt={`${displayLevel} anvil${decayLevel > 0 ? ' (decayed)' : ''}`}
            className="w-full h-full object-contain drop-shadow-lg"
            style={{
              filter: isLocked ? 'grayscale(70%) brightness(0.5)' : 'none'
            }}
          />
          
          {/* Moss overlay for decay */}
          {decayState.hasMoss && !isLocked && (
            <MossOverlay decayLevel={decayLevel} />
          )}
          
          {/* Lock overlay for locked state */}
          {isLocked && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Lock className="h-6 w-6 text-slate-400 drop-shadow-md" />
            </div>
          )}
          
          {/* Decay indicator badge */}
          {decayLevel > 0 && !isLocked && (
            <div 
              className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-red-600 border-2 border-slate-900 flex items-center justify-center"
              title={`Decay level: ${decayLevel}`}
            >
              <span className="text-[8px] font-bold text-white">!</span>
            </div>
          )}

          {/* Priority rank badge (personalized mode) */}
          {priority && priority.rank && !isLocked && (
            <div 
              className={`absolute -top-2 -left-2 w-6 h-6 rounded-full border-2 border-slate-900 flex items-center justify-center z-20 ${
                priority.rank <= 3 ? 'bg-gradient-to-br from-red-500 to-rose-600' :
                priority.rank <= 8 ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                'bg-gradient-to-br from-emerald-500 to-green-600'
              }`}
              title={`Priority #${priority.rank} — Score: ${priority.score}`}
              data-testid={`priority-badge-${lessonNumber}`}
            >
              <span className="text-[9px] font-bold text-white">{priority.rank}</span>
            </div>
          )}
        </div>
        
        {/* Pulse effect for current lesson */}
        {isCurrent && (
          <div 
            className="absolute inset-0 animate-ping pointer-events-none rounded-full"
            style={{ 
              background: 'radial-gradient(circle, rgba(251, 191, 36, 0.4) 0%, transparent 70%)',
              opacity: 0.5
            }}
          />
        )}
        
        {/* Gold sparkle - only if pristine gold (no decay) */}
        {displayLevel === 'gold' && decayLevel === 0 && (
          <Sparkles 
            className="absolute -top-2 -right-2 h-5 w-5 text-yellow-300 animate-pulse z-20" 
          />
        )}
        
        {/* Blacksmith marker for current lesson */}
        {isCurrent && blacksmithImage && (
          <div className="absolute -top-14 left-1/2 -translate-x-1/2 flex flex-col items-center z-30">
            <img 
              src={blacksmithImage}
              alt="Current position"
              className="h-10 w-10 object-contain animate-bounce drop-shadow-lg"
              style={{ 
                filter: 'drop-shadow(0 4px 8px rgba(251, 191, 36, 0.4))'
              }}
            />
            <div 
              className="w-0 h-0 -mt-1"
              style={{
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '8px solid #f97316'
              }}
            />
          </div>
        )}
      </button>

      {/* Floating Theme Label - alternates based on position */}
      <div 
        className={`
          absolute z-20 flex
          items-center gap-1 px-2.5 py-1 
          bg-slate-900/95 backdrop-blur-sm 
          rounded-md border border-slate-700/60 
          shadow-lg transition-all duration-300
          ${position === 'right' || position === 'center'
            ? 'right-full mr-2' 
            : 'left-full ml-2'}
          ${!isLocked ? 'hover:border-amber-600/40' : ''}
        `}
        style={{ top: '50%', transform: 'translateY(-50%)', whiteSpace: 'nowrap' }}
      >
        <span className="text-xs flex-shrink-0" role="img" aria-label={theme}>
          {themeEmoji}
        </span>
        <span className={`font-medium text-[10px] md:text-[11px] leading-tight ${isLocked ? 'text-slate-500' : decayLevel > 0 ? 'text-green-400' : 'text-slate-300'}`}>
          {translatedTheme}
        </span>
        {decayLevel > 0 && !isLocked && (
          <span className="text-[8px] text-red-400 ml-1 flex-shrink-0">{t('forge.decay') || 'decay'}</span>
        )}
        {priority && !isLocked && priority.reason !== 'already_mastered' && (
          <span className="text-[7px] text-purple-300 ml-1 flex-shrink-0">
            {Math.round((priority.mastery_pct || 0) * 100)}%
          </span>
        )}
        {priority && !isLocked && priority.reason === 'already_mastered' && (
          <span className="text-[7px] text-emerald-400 ml-1 flex-shrink-0">mastered</span>
        )}
      </div>
    </div>
  );
}
